import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { data: Array, today: Number, start: String, locale: String }

  connect() {
    this.ro = new ResizeObserver(() => this.render())
    this.ro.observe(this.element)
    this.render()
  }

  disconnect() {
    this.ro?.disconnect()
    this.tooltip?.remove()
    this.tooltip = null
  }

  render() {
    const w = this.element.getBoundingClientRect().width
    if (w < 4) return

    const data  = this.dataValue
    const today = this.todayValue
    const H = 96
    const n = data.length
    if (n === 0) return

    const max    = Math.max(1, ...data)
    const stepX  = w / n
    const cs     = getComputedStyle(document.documentElement)
    const accent = cs.getPropertyValue("--color-accent").trim()    || "#4338CA"
    const muted  = cs.getPropertyValue("--color-border").trim()    || "#E4E4E7"
    const t3     = cs.getPropertyValue("--color-fg-subtle").trim() || "#71717A"

    const guidelines = `
      <line x1="0" y1="${H}" x2="${w}" y2="${H}" stroke="${muted}"/>
      <line x1="0" y1="${H * 0.5}" x2="${w}" y2="${H * 0.5}" stroke="${muted}" stroke-dasharray="2 4" opacity="0.5"/>
    `

    let bars = ""
    let hits = ""
    data.forEach((total, i) => {
      const dayNum   = i + 1
      const isToday  = dayNum === today
      const isFuture = dayNum > today
      const h   = Math.max(1.5, (total / max) * (H - 6))
      const x   = i * stepX + stepX / 2
      const bw  = Math.max(2, Math.min(stepX * 0.55, 8))
      const clr = isFuture ? muted : (isToday ? accent : t3)
      const op  = isFuture ? 0.25 : (total === 0 ? 0.18 : 1)
      bars += `<rect x="${(x - bw / 2).toFixed(1)}" y="${(H - h - 1).toFixed(1)}" width="${bw.toFixed(1)}" height="${h.toFixed(1)}" rx="1.5" fill="${clr}" opacity="${op}"/>`
      hits += `<rect data-day-index="${i}" x="${(i * stepX).toFixed(1)}" y="0" width="${stepX.toFixed(1)}" height="${H}" fill="transparent" style="cursor:pointer"/>`
    })

    let todayMarker = ""
    if (today >= 1 && today <= n) {
      const tx  = ((today - 0.5) * stepX).toFixed(1)
      const tv  = data[today - 1] || 0
      const th  = Math.max(1.5, (tv / max) * (H - 6))
      const cy  = (H - th - 1).toFixed(1)
      todayMarker = `
        <line x1="${tx}" y1="0" x2="${tx}" y2="${H}" stroke="${accent}" stroke-width="1" stroke-dasharray="2 3" opacity="0.5"/>
        <circle cx="${tx}" cy="${cy}" r="3.5" fill="${accent}" stroke="white" stroke-width="1.5"/>
      `
    }

    this.element.innerHTML = `
      <svg width="${w}" height="${H}" style="display:block;overflow:visible">
        ${guidelines}${bars}${todayMarker}${hits}
      </svg>
    `

    this.bindHover()
  }

  bindHover() {
    const svg = this.element.querySelector("svg")
    if (!svg) return

    const onEnter = (e) => {
      const idx = Number(e.target.dataset.dayIndex)
      if (Number.isNaN(idx)) return
      this.showTooltip(idx, e.target)
    }
    const onLeave = () => this.hideTooltip()

    svg.querySelectorAll("[data-day-index]").forEach((el) => {
      el.addEventListener("mouseenter", onEnter)
      el.addEventListener("mouseleave", onLeave)
      el.addEventListener("touchstart", onEnter, { passive: true })
      el.addEventListener("touchend", onLeave)
    })
  }

  showTooltip(idx, hitEl) {
    const data  = this.dataValue
    const total = data[idx] || 0
    const date  = this.dateForIndex(idx)
    if (!date) return

    if (!this.tooltip) {
      this.tooltip = document.createElement("div")
      this.tooltip.style.cssText = [
        "position:absolute",
        "pointer-events:none",
        "z-index:30",
        "transform:translate(-50%,-100%)",
        "padding:6px 9px",
        "border-radius:8px",
        "background:var(--color-fg, #18181B)",
        "color:var(--color-bg, #FFF)",
        "font-size:11px",
        "font-weight:600",
        "line-height:1.3",
        "white-space:nowrap",
        "box-shadow:0 6px 18px rgba(0,0,0,0.18)",
        "opacity:0",
        "transition:opacity 120ms ease"
      ].join(";")
      this.element.appendChild(this.tooltip)
    }

    const locale = this.hasLocaleValue && this.localeValue ? this.localeValue : undefined
    const dayLabel = date.toLocaleDateString(locale, { day: "numeric", month: "short" })
    const amount   = this.formatAed(total)

    this.tooltip.innerHTML = `
      <div style="opacity:0.7;font-weight:500;text-transform:uppercase;letter-spacing:0.04em;font-size:10px">${dayLabel}</div>
      <div style="margin-top:2px;font-variant-numeric:tabular-nums">${amount}</div>
    `

    const hitRect = hitEl.getBoundingClientRect()
    const wrapRect = this.element.getBoundingClientRect()
    const left = hitRect.left + hitRect.width / 2 - wrapRect.left
    const top  = hitRect.top - wrapRect.top - 6
    this.tooltip.style.left = `${left}px`
    this.tooltip.style.top  = `${top}px`
    this.tooltip.style.opacity = "1"
  }

  hideTooltip() {
    if (this.tooltip) this.tooltip.style.opacity = "0"
  }

  dateForIndex(idx) {
    if (!this.hasStartValue || !this.startValue) return null
    const d = new Date(this.startValue)
    if (Number.isNaN(d.getTime())) return null
    d.setDate(d.getDate() + idx)
    return d
  }

  formatAed(cents) {
    const amount = (cents || 0) / 100
    const rounded = amount >= 100 ? Math.round(amount) : Math.round(amount * 100) / 100
    const decimals = amount >= 100 ? 0 : 2
    const locale = this.hasLocaleValue && this.localeValue ? this.localeValue : undefined
    const formatted = rounded.toLocaleString(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    })
    return `${formatted} AED`
  }
}
